import { createReducer, createSelector } from '@reduxjs/toolkit';
import { all, call, fork, takeEvery } from 'redux-saga/effects';
import {
  NOTIFICATIONCONTENT_INCLUDE,
  NOTIFICATIONCONTENT_TYPE,
} from '../../constants/api';
import { API_REDUCER } from '../../constants/store';
import {
  getNotificationContentSpecificationForHearingSuccess,
  updateNotificationContentInitialize,
  updateNotificationContentSuccess,
} from '../actions/api';
import { NotificationContentSpecificationConnector } from '../connectors/notificationContentSpecificationConnector';
import { getEntityId, getRelIdsOfType } from '../helpers/apiHelpers';
import { updateDtoList } from '../helpers/reducerHelpers';
import { generateCreateApiCall } from '../helpers/sagaHelpers';
import {
  getIncludedData,
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { notificiationContentSpecificationByIdSelector } from './notificationContentSpecificationSlice';

// -------- Overview --------
// The following endpoints exist
//  - PATCH /hearing/{hearingId}/notificationContentSpecification/{notificationContentSpecificationId}

// NotificationContent is included in the following endpoints
//  - GET /hearing/{hearingId}/notificationContentSpecification

// -------- Reducer --------
const notificationContentReducer = createReducer(
  {
    all: [],
  },
  (builder) => {
    builder
      .addCase(
        getNotificationContentSpecificationForHearingSuccess,
        (state, action) => {
          const data = getIncludedData(action);
          const notificationContents = data.filter(
            (include) => include.type === NOTIFICATIONCONTENT_INCLUDE,
          );
          return updateDtoList(state, 'all', notificationContents);
        },
      )
      .addCase(updateNotificationContentSuccess, (state, action) => {
        const notificationContents = getRequestData(action);
        return updateDtoList(state, 'all', notificationContents);
      });
  },
);

// ---------- SAGAS -----------
export function* notificationContentSaga() {
  yield all([fork(updateNotificationContentSaga)]);
}

function* updateNotificationContentSaga() {
  yield takeEvery(
    updateNotificationContentInitialize.type,
    generateCreateApiCall(NotificationContentSpecificationConnector, function* (
      connector,
      action,
    ) {
      return yield call(
        [connector, connector.updateNotificationContent],
        action.payload.hearingId,
        action.payload.notificationContentSpecificationId,
        action.payload.dtos,
      );
    }),
  );
}

// ---------- SELECTORS -----------
const selectAllNotificationContents = (state) =>
  selectAllFromApiState(state, API_REDUCER.NOTIFICATION_CONTENT);

const allNotificationContentsSelector = createSelector(
  selectAllNotificationContents,
  (notificationContents) => notificationContents,
);

const notificationContentByIdSelector = (id) =>
  createSelector(allNotificationContentsSelector, (notificationContents) =>
    notificationContents.find((nc) => getEntityId(nc) === id),
  );

const notificationContentForNotificationContentSpecificationWithIdSelector = (
  notificationContentSpecificationId,
) =>
  createSelector(
    [
      allNotificationContentsSelector,
      notificiationContentSpecificationByIdSelector(
        notificationContentSpecificationId,
      ),
    ],
    (notificationContents, notificationContentSpecification) => {
      var ncIds = getRelIdsOfType(
        notificationContentSpecification,
        NOTIFICATIONCONTENT_TYPE,
      );
      return notificationContents.filter((nc) =>
        ncIds.includes(getEntityId(nc)),
      );
    },
  );

export {
  notificationContentForNotificationContentSpecificationWithIdSelector,
  notificationContentByIdSelector,
};
export default notificationContentReducer;
